import { ChangeDetectionStrategy, Component, OnDestroy, signal } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ButtonV3Component, InputV3Component, SnackbarV3Service } from '@gea/digital-ui-lib';
import { DividerModule } from 'primeng/divider';
import { DataMaintenanceService } from '../data-maintenance.service';
import { Subject, takeUntil } from 'rxjs';
import { UtilityService } from '../../../shared/services/utility.service';

@Component({
  selector: 'app-rename-dialog',
  imports: [InputV3Component, ReactiveFormsModule, ButtonV3Component, DividerModule],
  templateUrl: './rename-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RenameDialogComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  currentFileNameNoExt: string = '';
  fileExtension: string = '';
  assetPath: string = '';
  isUserOverride: boolean = false;
  renamingFile = signal<boolean>(false);

  renameForm = this.formBuilder.group({
    currentFileName: new FormControl({ value: '', disabled: true }),
    newFileName: new FormControl('', [
      Validators.required,
      Validators.maxLength(255),
      this.utilityService.patternWithMessage(/^\w[\w|-]+$/, 'Use only letters, numbers, underscores, or hyphens.'),
    ]),
  });

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: DynamicDialogRef,
    private dialogConfig: DynamicDialogConfig,
    private dataMaintenanceService: DataMaintenanceService,
    private snackbarService: SnackbarV3Service,
    private readonly utilityService: UtilityService
  ) {
    const fullFileName: string = this.dialogConfig.data?.currentFileName ?? '';
    this.assetPath = this.dialogConfig.data?.assetPath ?? '';
    this.isUserOverride = this.dialogConfig.data?.isUserOverride ?? false;

    const { name, extension } = this.splitFileName(fullFileName);
    this.currentFileNameNoExt = name;
    this.fileExtension = extension;

    this.renameForm.get('currentFileName')?.setValue(this.currentFileNameNoExt);
  }

  save(): void {
    if (!this.renameForm.valid) {
      return;
    }

    const newNameNoExt = this.renameForm.get('newFileName')?.value;
    if (!newNameNoExt) return;

    this.renamingFile.set(true);
    this.dataMaintenanceService
      .renameUserAsset(this.assetPath, newNameNoExt, this.isUserOverride)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          const displayName = `${newNameNoExt}${this.fileExtension}`;
          this.snackbarService.add({
            severity: 'success',
            summary: 'Success',
            detail: `File renamed to "${displayName}" successfully.`,
          });
          this.renamingFile.set(false);
          this.dialogRef.close({ newFileName: displayName });
        },
        error: (err) => {
          this.renamingFile.set(false);
          console.error('Failed to rename file: ', err.message);
        },
      });
  }

  close(): void {
    this.dialogRef.close();
  }

  private splitFileName(fullFileName: string): { name: string; extension: string } {
    const compoundMatch = fullFileName.match(/\.[^.]+\.json$/i);
    if (compoundMatch && compoundMatch.index !== undefined) {
      return { name: fullFileName.substring(0, compoundMatch.index), extension: compoundMatch[0] };
    }

    const dotIndex = fullFileName.lastIndexOf('.');
    if (dotIndex > 0) {
      return { name: fullFileName.substring(0, dotIndex), extension: fullFileName.substring(dotIndex) };
    }

    return { name: fullFileName, extension: '' };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
